import FAQLink from "./FAQLink";
import style from "./GuidelineOverview.module.scss";
import PopularTopics from "./PopularTopics";
import { useLocation, useNavigate } from "react-router-dom";
import useGuidelineMenus from "../../../hooks/useGuidelineMenus";
import { handleClickGlDevelopment } from "./resourceLinkHandler";
import classNames from "classnames";
import { useEffect, useState } from "react";
import AcknowledgementContent from "./AcknowledgementContent";
import ExpertGroupContent from "./ExpertGroupContent";
import { selectMap } from "../../counter/counterSlice";
import { useSelector } from "react-redux";
import generateMenuFromXml from "../../../components/GuidelineNav/menuGeneration";
import { isPregnancyAndBreastfeedingPage } from "../../../utils/isPregnancyAndBreastfeedingPage";
import { getGuidelineUpdatesDitamapPath } from "../../../utils/guidelineAbbreviations";
import { isValidBookmapContent } from "../../../utils/ditaUtils";
import { selectDitaContent } from "../../../components/Content/ContentSlice";
import { contentThunk } from "../../../components/Content/ContentThunk";
import useGuidelineDitaUpdates from "../../../hooks/useGuidelineDitaUpdates";
import { useDispatch } from "react-redux";

const generateSelfSearch = (pathname) => {
  const arraySearch = pathname
    .replace("/guidelines/", "")
    .replace("/app/nav/", "")
    .replace("Pregnancy_and_Breastfeeding", "quicklinks")
    .split("/");
  const [glPage, ...rest] = arraySearch;
  return { glPage, submenu: rest };
};

const GuidelineOverview = () => {
  const { pathname } = useLocation();
  const guidelineObj = generateSelfSearch(pathname);
  const navigate = useNavigate();
  const dartMap = useSelector(selectMap);
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();
  const [acknowledgementLink, setAcknowledgementLink] = useState();
  const [expertGroupsLink, setExpertGroupsLink] = useState();

  const { resources } = useGuidelineMenus(guidelineObj);

  const handleTgLink = () => {
    window.open(
      "https://www.tg.org.au/what-we-do/governance/conflict-of-interest-policy/",
      "_blank",
      "noopener,noreferrer",
    );
  };

  // values: "acknowledgement" | "expertGroups" | null
  const [activePanel, setActivePanel] = useState(null);

  // Check if this guideline has DITA updates
  const hasDitaUpdates = useGuidelineDitaUpdates(guidelineObj.glPage);

  const handleViewAcknowledgements = () => {
    setActivePanel((prev) => (prev === "acknowledgement" ? null : "acknowledgement"));
  };

  const handleViewExpertGroups = () => {
    setActivePanel((prev) => (prev === "expertGroups" ? null : "expertGroups"));
  };

  useEffect(() => {
    if (!dartMap) return;

    try {
      const parsed = generateMenuFromXml(`${dartMap}`);

      if (!parsed?.relatedLinks) return;

      parsed.relatedLinks.forEach((link) => {
        const href = link.href?.toLowerCase();
        if (!href) return;

        const cleanedHref = link.href.replace(/^(\.\.\/)+/, "");

        if (href.endsWith("_acknowledgement.dita")) {
          setAcknowledgementLink(cleanedHref);
        }

        if (href.endsWith("_egm.dita")) {
          setExpertGroupsLink(cleanedHref);
        }
      });
    } catch (err) {
      console.error("Error extracting relatedLinks:", err);
    }
  }, [dartMap]);

  const handleOnClick = (name) => {
    // Special handling for "whats_new" - check for DITA updates
    if (name === "whats_new") {
      handleWhatsNewClick();
      return;
    }

    const item = filterResourceLink(name);
    if (item.isExternal) {
      window.open(item.msPath, "_blank", "noopener,noreferrer");
    } else {
      navigate(item.msPath);
    }
  };

  const handleWhatsNewClick = () => {
    const guidelineName = guidelineObj.glPage;

    const ditamapPath = getGuidelineUpdatesDitamapPath(guidelineName);

    if (!ditamapPath) {
      // No abbreviation found, fallback to external PDF
      const item = filterResourceLink("whats_new");
      if (item?.isExternal) {
        window.open(item.msPath, "_blank", "noopener,noreferrer");
      }
      return;
    }

    const updateContent = content?.content?.find((c) => c.ditafile === ditamapPath);

    if (updateContent && isValidBookmapContent(updateContent.content)) {
      // DITA updates exist in cache, navigate to the new updates page
      navigate(`/guidelines/${guidelineName}/updates`);
    } else {
      dispatch(contentThunk(ditamapPath))
        .then((result) => {
          if (isValidBookmapContent(result.payload)) {
            navigate(`/guidelines/${guidelineName}/updates`);
          } else {
            // No DITA updates or fetch failed, fallback to external PDF
            const item = filterResourceLink("whats_new");
            if (item?.isExternal) {
              window.open(item.msPath, "_blank", "noopener,noreferrer");
            }
          }
        })
        .catch(() => {
          const item = filterResourceLink("whats_new");
          if (item?.isExternal) {
            window.open(item.msPath, "_blank", "noopener,noreferrer");
          }
        });
    }
  };

  const filterResourceLink = (name) => {
    return resources.find((item) => {
      return item.key.toLowerCase() === name;
    });
  };

  const hideForPbsPage = () => {
    return !isPregnancyAndBreastfeedingPage(pathname);
  };

  return (
    <div className={style.guidelineOverview}>
      <div className={style.leftPanel}>
        {activePanel === null && (
          <>
            {hideForPbsPage() && (
              <div
                className={classNames(style.linkItem, style.guidelineUpdate, {
                  [style.internal]: hasDitaUpdates,
                })}
                onClick={() => handleOnClick("whats_new")}
              >
                <div className={"size-18 semibold"}>Updates</div>
                {hasDitaUpdates !== null && (
                  <i className={hasDitaUpdates ? "tg-icons-LeftArrow" : "tg-icons-Open"}></i>
                )}
              </div>
            )}
            <div className={style.hideAcknowledgements}>
              <div className="size-18 semibold">Expert group</div>
            </div>
            <div className={style.boxedSection}>
              <div className={style.extraParagraph}>
                Members of the expert groups have provided declarations of interest as per
                Therapeutic Guidelines Limited's policy on conflict of interest. For more
                information on the conflicts of interest policy, see the{" "}
                <span className={style.staticLink} onClick={handleTgLink}>
                  Therapeutic Guidelines Limited website
                </span>
                .
              </div>

              <div className={style.extendButtonSection}>
                <button
                  className={style.toggleButton}
                  data-testid="show-all"
                  onClick={handleViewExpertGroups}
                >
                  <span className={classNames("size-16", style.viewText)}>View</span>
                  <i className="fa banner-head-topic fa-angle-down"></i>
                </button>
              </div>
            </div>

            <div className={style.hideAcknowledgements}>
              <div className={"size-18 semibold"}>Acknowledgements</div>
            </div>
            <div className={style.boxedSection}>
              <div className={style.extraParagraph}>
                The expert group thanks colleagues who have contributed to the development of the
                guidelines. In particular, they'd like to thank the individuals listed below.
              </div>
              <div className={style.extendButtonSection}>
                <button className={style.toggleButton} onClick={handleViewAcknowledgements}>
                  <span className={classNames("size-16", style.viewText)}>View</span>
                  <i className="fa banner-head-topic fa-angle-down"></i>
                </button>
              </div>
            </div>
          </>
        )}
        <AcknowledgementContent
          onClickCollapseButton={handleViewAcknowledgements}
          doiLink={filterResourceLink("declaration_of_interest")?.msPath}
          showExtraInfo={hideForPbsPage()}
          showCollapseButton={true}
          link={acknowledgementLink}
          isOpen={activePanel === "acknowledgement"}
        />

        <div className={style.leftPanelContent}>
          <ExpertGroupContent
            onClickCollapseButton={handleViewExpertGroups}
            link={expertGroupsLink}
            isOpen={activePanel === "expertGroups"}
            showCollapseButton={true}
          />
        </div>
      </div>

      <div className={style.rightPanel}>
        <div className={style.rightPanelContent}>
          <PopularTopics glObj={guidelineObj} />
          <FAQLink></FAQLink>
          <div className={style.supportLink}>
            <div className={classNames("size-18 semibold")}>Support links</div>
            <div className={style.linkItem} onClick={handleClickGlDevelopment}>
              Guideline development
              <i className="tg-icons-Open"></i>
            </div>
            {hideForPbsPage() && (
              <div
                className={style.linkItem}
                onClick={() => handleOnClick("declaration_of_interest")}
              >
                Declaration of interest
                <i className="tg-icons-Open"></i>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidelineOverview;
